import React, { FC, useState, useEffect } from "react";
import { Button, Collapse, Spin, Modal } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  PushpinOutlined,
  PushpinFilled,
} from "@ant-design/icons";
import { AccruedFee, RelevantMatter } from "../../data-types";
import { useQuery } from "@apollo/client";
import { FEE_EARNER_LEVELS_QUERY, GET_PHASES, GET_TAX_INFO_FOR_MATTER } from "~/graphql/query";
import { GetPhasesQuery, GetPhasesQueryVariables, RateReplacement, TaxLabel } from "~/gql/graphql";
import { HistoricInfo } from "../historic-info";
import { useSettings } from "~/hooks/settings";
import styles from "./create-proposal.css";
import { useTaxonomyLabel } from "~/context/taxonomy-context";
import { TeamMembership } from "../../data-types";
import { DeprecatedRatesContent } from "../deprecated-rates/deprecated-rates-content";

const {
  "matter-metadata": matterMetadataClassName,
  "matter-metadata-item": matterMetadataItemClassName,
  "matter-metadata-label": matterMetadataLabelClassName,
  "matter-metadata-value": matterMetadataValueClassName,
  "matter-metadata-separator": matterMetadataSeparatorClassName,
  "matter-creation-info": matterCreationInfoClassName,
  "lost-icon": lostIconClassName,
  "won-icon": wonIconClassName,
} = styles;

interface RelevantMatterPageProps {
  matter: RelevantMatter;
  accruedFees?: AccruedFee;
  onDeselectMatter: () => void;
  maxHeight: string;
  onCopyScope?: () => void;
  onCopyResourcing?: (rateReplacements: RateReplacement[]) => void;
  onCopyPricing?: (rateReplacements: RateReplacement[]) => void;
  onCopyAll?: (rateReplacements: RateReplacement[]) => void;
  onPinToMatter?: (matterId: number) => void;
  pinned?: boolean;
  showAlert?: boolean;
}

export const RelevantMatterPage: FC<RelevantMatterPageProps> = ({
  matter,
  accruedFees,
  onDeselectMatter,
  maxHeight,
  onCopyScope,
  onCopyResourcing,
  onCopyPricing,
  onCopyAll,
  onPinToMatter,
  pinned,
  showAlert = true,
}) => {
  const [isCopyToMatterModalOpen, setIsCopyToMatterModalOpen] = useState(false);
  const [deprecatedRates, setDeprecatedRates] = useState<TeamMembership[]>([]);
  const [rateReplacements, setRateReplacements] = useState<RateReplacement[]>([]);

  const taxonomy = useTaxonomyLabel();

  const { loading: loadingPhaseData, data: phaseData } = useQuery<
    GetPhasesQuery,
    GetPhasesQueryVariables
  >(GET_PHASES, {
    variables: { matterId: matter.id },
  });
  const { data: taxInfo } = useQuery(GET_TAX_INFO_FOR_MATTER, {
    variables: { matterId: matter.id },
  });
  const { data: feeEarnerLevelsData = undefined, loading: feeEarnerLevelsLoading } =
    useQuery(FEE_EARNER_LEVELS_QUERY);
  const { settings } = useSettings();
  const defaultCurrency = settings?.currency;
  const taxLabel = (taxInfo?.getTaxInfoForMatter?.taxLabel || settings?.taxLabel) ?? "";

  const totalDuration =
    accruedFees?.accruedTimes.reduce((sum, time) => sum + time.actualDuration, 0) || 1;

  useEffect(() => {
    if (matter && matter.teamMemberships && !feeEarnerLevelsLoading) {
      const deprecated = matter.teamMemberships?.filter(
        (tm) => tm.rate.deprecated || tm.feeEarner?.deprecated
      );
      // assign feeEarnerLevels manually since they will be missing on deprecated rates (middleware)
      const deprecatedRatesWithFeeEarnerLevels = deprecated?.map((tm) => ({
        ...tm,
        rate: {
          ...tm.rate,
          feeEarnerLevel: feeEarnerLevelsData?.feeEarnerLevels.records.find(
            (level) => level.id === tm.rate.feeEarnerLevelId
          ),
        },
      }));
      setDeprecatedRates(deprecatedRatesWithFeeEarnerLevels || []);
    }
  }, [matter?.teamMemberships, feeEarnerLevelsLoading]);

  const handleCopyToMatter = () => {
    if (onCopyResourcing && onCopyPricing && onCopyAll) {
      setIsCopyToMatterModalOpen(true);
    } else if (onCopyScope) {
      onCopyScope();
    }
  };

  const PA_LABEL = taxonomy.PracticeArea;
  const FE_LABEL = taxonomy.FeeEarner;

  const handleModalClose = () => {
    setIsCopyToMatterModalOpen(false);
  };

  const handleCopyScope = () => {
    onCopyScope && onCopyScope();
    handleModalClose();
  };

  const handleCopyResourcing = () => {
    onCopyResourcing && onCopyResourcing(rateReplacements);
    handleModalClose();
  };

  const handleCopyPricing = () => {
    onCopyPricing && onCopyPricing(rateReplacements);
    handleModalClose();
  };

  const handleCopyAll = () => {
    onCopyAll && onCopyAll(rateReplacements);
    handleModalClose();
  };

  const handleReplaceTeamMember = (data: {
    teamMemberToAdd: {
      feeEarnerId: number | null;
      feeEarnerLevelId: number | null;
      rateId: number | null;
    };
    teamMemberToRemove: {
      feeEarnerId: number | null;
      rateId: number | null;
    };
  }) => {
    const newReplacement: RateReplacement = {
      oldRateId: data.teamMemberToRemove.rateId as number,
      newRateId: data.teamMemberToAdd.rateId as number,
      newFeeEarnerId: data.teamMemberToAdd.feeEarnerId,
      newFeeEarnerLevelId: data.teamMemberToAdd.feeEarnerLevelId,
    };

    setRateReplacements((prev) => {
      // Replace existing replacement if there's one for the same old rate
      const existingIndex = prev.findIndex((r) => r.oldRateId === newReplacement.oldRateId);

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newReplacement;
        return updated;
      }

      return [...prev, newReplacement];
    });
  };

  return (
    <>
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          marginTop: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Button onClick={onDeselectMatter}>Back</Button>
            <h3 style={{ margin: 0 }}>{matter.title}</h3>
          </div>
          {onCopyScope && (
            <div style={{ display: "flex", gap: "12px" }}>
              <Button
                type={pinned ? "primary" : "default"}
                icon={pinned ? <PushpinFilled /> : <PushpinOutlined />}
                onClick={() => onPinToMatter?.(matter.id)}
              >
                {pinned ? "Unpin from matter" : "Pin to matter"}
              </Button>
              <Button type="primary" onClick={handleCopyToMatter}>
                {onCopyResourcing && onCopyPricing && onCopyAll ? "Copy to matter" : "Copy scope"}
              </Button>
            </div>
          )}
        </div>
        <div className={matterMetadataClassName}>
          {matter.client && (
            <div className={matterMetadataItemClassName}>
              <span className={matterMetadataLabelClassName}>Client: </span>
              <span className={matterMetadataValueClassName}>{matter.client.name}</span>
            </div>
          )}
          {matter.client && matter.organisationalUnit && (
            <span className={matterMetadataSeparatorClassName}>•</span>
          )}
          {matter.organisationalUnit && (
            <div className={matterMetadataItemClassName}>
              <span className={matterMetadataLabelClassName}>{PA_LABEL}: </span>
              <span className={matterMetadataValueClassName}>{matter.organisationalUnit.name}</span>
            </div>
          )}
          {matter.status &&
            (matter.status === "Lost" ||
              matter.status === "Closed" ||
              matter.status === "Live") && (
              <>
                <span className={matterMetadataSeparatorClassName}>•</span>
                <div className={matterMetadataItemClassName}>
                  {matter.status === "Lost" ? (
                    <CloseCircleFilled className={lostIconClassName} />
                  ) : (
                    <CheckCircleFilled className={wonIconClassName} />
                  )}
                  <span
                    className={matterMetadataValueClassName}
                    style={{
                      color: matter.status === "Lost" ? "#ff4d4f" : "#52c41a",
                    }}
                  >
                    {matter.status === "Lost" ? "Lost" : "Won"}
                  </span>
                </div>
              </>
            )}
        </div>
        {!matter.template && (
          <div className={matterCreationInfoClassName}>
            <div className={matterMetadataItemClassName}>
              <span className={matterMetadataLabelClassName}>Created on </span>
              <span className={matterMetadataValueClassName}>
                {matter.createdAt && new Date(matter.createdAt).toLocaleDateString()}
              </span>
              {matter.createdAt && matter.createdBy?.displayName && (
                <>
                  <span className={matterMetadataLabelClassName} style={{ marginLeft: "4px" }}>
                    by
                  </span>
                  <span className={matterMetadataValueClassName}>
                    {matter.createdBy.displayName}
                  </span>
                </>
              )}
            </div>
          </div>
        )}
        <div style={{ paddingBottom: 0, maxHeight: maxHeight, overflowY: "auto" }}>
          {accruedFees ? (
            <div style={{ marginBottom: "12px" }}>
              <HistoricInfo
                matterId={matter.id}
                data={{
                  ...accruedFees,
                  actualGpm: accruedFees.actualGpm,
                  gpmDangerThreshold: accruedFees.gpmDangerThreshold,
                  gpmWarningThreshold: accruedFees.gpmWarningThreshold,
                  agreedPrice: accruedFees.agreedPrice,
                  taxLabel: taxLabel as TaxLabel,
                  accruedTimes: accruedFees.accruedTimes.map((time) => ({
                    ...time,
                    feeEarnerName: time.feeEarnerName ?? "",
                    durationAsPercentageOfTotalMatterDuration:
                      (time.actualDuration / totalDuration) * 100,
                  })),
                }}
                showAlert={showAlert}
              />
            </div>
          ) : null}
          {loadingPhaseData ? (
            <Spin tip="Loading phases..." />
          ) : phaseData?.getPhases && phaseData?.getPhases.length > 0 ? (
            <>
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "12px" }}
              >
                <h3 style={{ margin: "10px 0" }}>Scope</h3>
              </div>{" "}
              <Collapse style={{ marginBottom: "12px" }}>
                {phaseData?.getPhases.map((phase) => (
                  <Collapse.Panel header={phase.title} key={phase.id}>
                    <ul>
                      {phase.tasks?.map((task) => (
                        <li key={task.id}>{task.title}</li>
                      ))}
                    </ul>
                  </Collapse.Panel>
                ))}
              </Collapse>
            </>
          ) : null}
        </div>
      </div>
      <Modal
        title="Copy Matter Details"
        open={isCopyToMatterModalOpen}
        onCancel={() => setIsCopyToMatterModalOpen(false)}
        footer={null}
        width={deprecatedRates.length > 0 ? 1000 : 700}
      >
        {deprecatedRates.length > 0 ? (
          <>
            <p>
              This matter contains deprecated rates or {FE_LABEL.toLowerCase()}s that need to be
              replaced before copying:
            </p>
            <DeprecatedRatesContent
              deprecatedRates={deprecatedRates}
              matterId={matter.id}
              clientId={matter.client?.id}
              organisationalUnitId={matter.organisationalUnit?.id}
              currency={matter.currency || defaultCurrency || { code: "", symbol: "" }}
              pricingType={matter.pricingType}
              onReplaceTeamMember={handleReplaceTeamMember}
              replacedRateIds={rateReplacements.map((replacement) => replacement.oldRateId)}
              tableHeight={250}
            />
            <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <div style={{ display: "flex", gap: "12px" }}>
                <Button onClick={handleCopyScope}>Copy Scope</Button>
                <Button
                  onClick={handleCopyResourcing}
                  disabled={
                    deprecatedRates.length > 0 && rateReplacements.length < deprecatedRates.length
                  }
                >
                  Copy Resourcing
                </Button>
                <Button
                  onClick={handleCopyPricing}
                  disabled={
                    deprecatedRates.length > 0 && rateReplacements.length < deprecatedRates.length
                  }
                >
                  Copy Pricing
                </Button>
                <Button
                  onClick={handleCopyAll}
                  type="primary"
                  disabled={
                    deprecatedRates.length > 0 && rateReplacements.length < deprecatedRates.length
                  }
                >
                  Copy All
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p>
              Select which details you would like to copy from this matter to the current proposal:
            </p>
            <ul>
              <li>
                Copy scope will copy phases, tasks, assumptions, exclusions, and disbursements.
              </li>
              <li>Copy resourcing will copy team members and rates.</li>
              <li>
                Copy pricing will copy all pricing options and {FE_LABEL.toLowerCase()} hours.
              </li>
            </ul>
            <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <div style={{ display: "flex", gap: "12px" }}>
                <Button onClick={handleCopyScope}>Copy Scope</Button>
                <Button onClick={handleCopyResourcing}>Copy Resourcing</Button>
                <Button onClick={handleCopyPricing}>Copy Pricing</Button>
                <Button onClick={handleCopyAll} type="primary">
                  Copy All
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};
